import * as Styled from "./SmallCard.styled";
import { StarRating } from "../../star-rating";
import { IPropsMediaCardExtended } from "../../../types/CardTypes";

export const SmallCard: React.FC<IPropsMediaCardExtended> = ({
    mediaItem,
    mediaRating,
    cardClick,
    enableRatingControls,
    onRatingSuccess,
    isSignedIn,
    hasEligiblePlan,
    sessionToken,
    isSelected,
    onMouseEnter,
    onMouseLeave,
    applyResponsive = false,
    autoWidth = false,
}) => {
    /**
     * @function handlePosterOver
     * @description Poster mouseenter event handler.
     * @param {Object} evt
     */
    function handlePosterOver(evt: React.MouseEvent<any>): void {
        evt.currentTarget.focus();
        let targetParent = evt.currentTarget.closest(".media-card");
        let targetPoster = targetParent.querySelector(".media-poster");
        let targetAncillary = targetParent.querySelector(".media-ancillary");
        targetParent.classList.add("hover-intent");
        targetPoster.classList.add("hover-intent");
        targetAncillary.classList.add("hover-intent");
    }

    /**
     * @function handlePosterOut
     * @description Poster mouseleave event handler.
     * @param {Object} evt
     */
    function handlePosterOut(evt: React.MouseEvent<any>): void {
        let targetParent = evt.currentTarget.closest(".media-card");
        let targetPoster = targetParent.querySelector(".media-poster");
        let targetAncillary = targetParent.querySelector(".media-ancillary");
        targetParent.classList.remove("hover-intent");
        targetPoster.classList.remove("hover-intent");
        targetAncillary.classList.remove("hover-intent");
    }

    /**
     * @function handlePosterFocus
     * @description Poster focus event handler.
     * @param {Object} evt
     */
    function handlePosterFocus(evt: React.FocusEvent<any>): void {
        let targetParent = evt.currentTarget.closest(".media-card");
        let targetPoster = targetParent.querySelector(".media-poster");
        let targetAncillary = targetParent.querySelector(".media-ancillary");
        targetParent.classList.add("hover-intent");
        targetPoster.classList.add("hover-intent");
        targetAncillary.classList.add("hover-intent");
    }

    /**
     * @function handlePosterBlur
     * @description Poster blur event handler.
     * @param {Object} evt
     */
    function handlePosterBlur(evt: React.FocusEvent<any>) {
        if (
            !evt.currentTarget.parentNode.parentNode.contains(evt.relatedTarget)
        ) {
            let targetParent = evt.currentTarget.closest(".media-card");
            let targetPoster = targetParent.querySelector(".media-poster");
            let targetAncillary =
                targetParent.querySelector(".media-ancillary");
            targetParent.classList.remove("hover-intent");
            targetPoster.classList.remove("hover-intent");
            targetAncillary.classList.remove("hover-intent");
        }
    }

    return (
        <Styled.MediaCard
            className={`media-card`}
            onMouseEnter={onMouseEnter}
            onMouseLeave={onMouseLeave}
        >
            <Styled.MediaCardWrapper
                onClick={(e) => {
                    e.stopPropagation();
                    cardClick(mediaItem);
                }}
                className={`media-card--wrapper`}
                isSelected={isSelected}
            >
                <Styled.MediaPoster
                    className={`media-poster`}
                    applyResponsive={applyResponsive}
                    autoWidth={autoWidth}
                >
                    <Styled.MediaPosterLink
                        onClick={() => cardClick(mediaItem)}
                        onMouseEnter={(evt) => handlePosterOver(evt)}
                        onMouseLeave={(evt) => handlePosterOut(evt)}
                        onFocus={(evt) => handlePosterFocus(evt)}
                        onBlur={(evt) => handlePosterBlur(evt)}
                        className="media-poster--link"
                        role="button"
                        tabIndex={0}
                    ></Styled.MediaPosterLink>

                    <Styled.MediaPosterArt className="media-poster--art">
                        <Styled.MediaPosterImage
                            src={mediaItem.thumbnail}
                            className={`media-poster--image`}
                            alt="MediaCard Thumbnail"
                        ></Styled.MediaPosterImage>
                    </Styled.MediaPosterArt>
                </Styled.MediaPoster>

                <Styled.MediaAncillary
                    onMouseEnter={(evt) => handlePosterOver(evt)}
                    onMouseLeave={(evt) => handlePosterOut(evt)}
                    className={`media-ancillary`}
                >
                    <Styled.MediaMetadata className="media-metadata">
                        <div style={{ color: "rgba(255,255,255, 1)" }}>
                            {mediaItem.title}
                        </div>
                    </Styled.MediaMetadata>

                    {enableRatingControls && isSignedIn && hasEligiblePlan && (
                        <Styled.MediaRating>
                            <StarRating
                                mediaItem={mediaItem}
                                mediaRating={mediaRating?.rating ?? 0}
                                sessionToken={sessionToken}
                                onRatingSuccess={onRatingSuccess}
                            />
                        </Styled.MediaRating>
                    )}

                    {mediaItem.description &&
                        mediaItem.description.length > 0 && (
                            <Styled.MediaSynopsis className="media-synopsis">
                                {mediaItem.description}
                            </Styled.MediaSynopsis>
                        )}

                    {mediaItem.genre && mediaItem.genre.length > 0 && (
                        <Styled.MediaGenre className="media-genre">
                            {mediaItem.genre.replace(/,(?=[^\s])/g, ", ")}
                        </Styled.MediaGenre>
                    )}
                    {((mediaItem.directors && mediaItem.directors.length > 0) ||
                        (mediaItem.producers &&
                            mediaItem.producers.length > 0)) && (
                        <Styled.MediaCrew className="media-crew">
                            {mediaItem.directors &&
                                mediaItem.directors.length > 0 && (
                                    <Styled.CrewItem>
                                        <span>Directed by:</span>{" "}
                                        {mediaItem.directors.replace(
                                            /,(?=[^\s])/g,
                                            ", "
                                        )}
                                    </Styled.CrewItem>
                                )}
                            {mediaItem.producers &&
                                mediaItem.producers.length > 0 && (
                                    <Styled.CrewItem>
                                        <span>Produced by:</span>{" "}
                                        {mediaItem.producers.replace(
                                            /,(?=[^\s])/g,
                                            ", "
                                        )}
                                    </Styled.CrewItem>
                                )}
                        </Styled.MediaCrew>
                    )}
                </Styled.MediaAncillary>
            </Styled.MediaCardWrapper>
        </Styled.MediaCard>
    );
};
